import authHeader from "@/services/auth/AuthHeader";
import axios from "axios";

// Android device management (AMAPI) — SUPER_ADMIN only (server-enforced via /api/superadmin/**).
// Backs the System -> MDM console: device list + remote commands, policies, enrollment QR codes.
const BASE = "/api/superadmin/androidmanagement";

class MdmService {
    // Safe to call even when AMAPI is unconfigured → {configured, canBootstrapEnterprise, enterpriseId}.
    getStatus(){
        return axios.get(BASE + "/status", { headers: authHeader() });
    }

    // Masked config (NO service-account JSON) → {enterpriseId, projectId, credentialsConfigured, *Source}.
    getConfig(){
        return axios.get(BASE + "/config", { headers: authHeader() });
    }

    // Persist {credentialsJson?, enterpriseId, projectId}. Blank credentialsJson keeps the stored key.
    saveConfig(payload){
        return axios.post(BASE + "/config", payload, { headers: authHeader() });
    }

    // Device-quota status → {quota, enrolled, utilizationPct, warnThresholdPct, shouldApply}.
    getQuotaStatus(){
        return axios.get(BASE + "/quota", { headers: authHeader() });
    }

    // Self-hosted APK-hosting config (license public key, private S3 bucket, folder) — Couchbase-stored,
    // edited on the AMAPI Config page. Backs GET /api/public/apk/** (see app-distribution-runbook).
    getApkConfig(){
        return axios.get("/api/superadmin/play/apkConfig", { headers: authHeader() });
    }

    saveApkConfig(payload){
        return axios.post("/api/superadmin/play/apkConfig", payload, { headers: authHeader() });
    }

    // Upload a signed release APK to the self-hosted hosting (private S3 bucket / local folder fallback).
    // formData = FormData {packageName, file}; onProgress = axios upload-progress callback (optional).
    uploadApk(formData, onProgress){
        return axios.post("/api/superadmin/play/uploadApk", formData, {
            headers: authHeader(),
            onUploadProgress: onProgress
        });
    }

    // Overview of the hosted APK versions -> {storedIn, apks: [{packageName, versionCode, versionName,
    // label, bytes, lastModifiedMs, sha256Base64, active}]} (newest first per package).
    listApks(){
        return axios.get("/api/superadmin/play/listApks", { headers: authHeader() });
    }

    // Make a hosted version the one the stable download URL serves (re-activate an older one = rollback).
    activateApk(packageName, versionCode){
        return axios.post("/api/superadmin/play/activateApk", { packageName, versionCode }, { headers: authHeader() });
    }

    // Delete a hosted version (the ACTIVE version is refused server-side).
    deleteApk(packageName, versionCode){
        return axios.post("/api/superadmin/play/deleteApk", { packageName, versionCode }, { headers: authHeader() });
    }

    // The managed-Play externally-hosted definition file for a hosted version, generated server-side
    // from the hosted bytes (upload it in Play Console as the release). Returned as a blob download.
    downloadApkDefinition(packageName, versionCode){
        return axios.get("/api/superadmin/play/apkDefinition", {
            headers: authHeader(),
            params: { packageName, versionCode },
            responseType: "blob"
        });
    }

    // Paged device list: returns { devices, nextPageToken }. Pass the token to fetch the next page.
    listDevices(pageSize, pageToken){
        return axios.get(BASE + "/devices", { headers: authHeader(), params: { pageSize, pageToken } });
    }

    // type: REBOOT | LOCK | RESET_PASSWORD | RELINQUISH_OWNERSHIP | START_LOST_MODE | STOP_LOST_MODE | CLEAR_APP_DATA
    deviceCommand(deviceId, type){
        return axios.post(BASE + "/devices/" + encodeURIComponent(deviceId) + "/command",
            { type: type }, { headers: authHeader() });
    }

    deleteDevice(deviceId){
        return axios.delete(BASE + "/devices/" + encodeURIComponent(deviceId), { headers: authHeader() });
    }

    // Factory-reset (wipe) a device — AMAPI delete with wipe flags. Irreversible; the device is unenrolled.
    wipeDevice(deviceId){
        return axios.post(BASE + "/devices/" + encodeURIComponent(deviceId) + "/wipe", {}, { headers: authHeader() });
    }

    // Mark/unmark a device as RMA (a note only; the device is NOT deleted from AMAPI).
    setDeviceRma(deviceId, marked, note){
        return axios.post(BASE + "/devices/" + encodeURIComponent(deviceId) + "/rma", { marked, note }, { headers: authHeader() });
    }

    // Toggle a device's kiosk lock. enabled=false -> move to MAINTENANCE (unlocked); true -> back to its kiosk policy.
    setKioskMode(deviceId, enabled, returnPolicy){
        return axios.post(BASE + "/devices/" + encodeURIComponent(deviceId) + "/kioskMode", { enabled, returnPolicy }, { headers: authHeader() });
    }

    // ---- Staged rollout: a few merchants first, the fleet once the build has proved itself. ----
    getRollout(){
        return axios.get(BASE + "/rollout", { headers: authHeader() });
    }

    startRolloutWave(packageName, merchants){
        return axios.post(BASE + "/rollout/wave", { packageName, merchants }, { headers: authHeader() });
    }

    releaseRolloutToFleet(packageName){
        return axios.post(BASE + "/rollout/release", { packageName }, { headers: authHeader() });
    }

    abortRolloutWave(packageName){
        return axios.post(BASE + "/rollout/abort", { packageName }, { headers: authHeader() });
    }

    // Which enrolled devices a wave would touch, and the version each runs — the blast radius, shown
    // before anything is pressed.
    getRolloutDevices(packageName, merchants){
        return axios.post(BASE + "/rollout/devices", { packageName, merchants }, { headers: authHeader() });
    }

    // Force an app to update NOW by uninstalling and reinstalling it through the policy — the only
    // mechanism AMAPI has (Play's background updater has never delivered a release to the fleet).
    // WIPES the app's data, so the terminal must be able to log itself back in, or be idle.
    // Three steps: start -> poll until the app is gone -> finish (which reinstalls the current release).
    startForceUpdate(deviceId, packageName){
        return axios.post(BASE + "/devices/" + encodeURIComponent(deviceId) + "/forceUpdate", { packageName }, { headers: authHeader() });
    }

    getForceUpdateStatus(deviceId){
        return axios.get(BASE + "/devices/" + encodeURIComponent(deviceId) + "/forceUpdate", { headers: authHeader() });
    }

    finishForceUpdate(deviceId){
        return axios.post(BASE + "/devices/" + encodeURIComponent(deviceId) + "/forceUpdate/finish", {}, { headers: authHeader() });
    }

    // Set (or clear, when name is blank) a device's user-defined display name (Couchbase-stored).
    setDeviceName(deviceId, name){
        return axios.post(BASE + "/deviceName", { deviceId, name }, { headers: authHeader() });
    }

    // Set (or clear, when blank) a device's RustDesk ID — recorded at staging (the stock RustDesk client
    // auto-generates its ID; no deterministic ID / managed configuration). Used by «Fjern-styring».
    setRustdeskId(deviceId, rustdeskId){
        return axios.post(BASE + "/rustdeskId", { deviceId, rustdeskId }, { headers: authHeader() });
    }

    // Reassign a device to a different policy (POS_DESKTOP, POS_MOBILE, KIOSK, KDS).
    setDevicePolicy(deviceId, policyId){
        return axios.post(BASE + "/devices/" + encodeURIComponent(deviceId) + "/policy", { policyId }, { headers: authHeader() });
    }

    // Is a package distributed to OUR enterprise in managed Google Play? -> {available, app|message}.
    // available:false (AMAPI 404) = devices cannot license/install it, however correct the hosting + policy are.
    checkAppAvailability(packageName){
        return axios.get(BASE + "/applications/" + encodeURIComponent(packageName), { headers: authHeader() });
    }

    listPolicies(){
        return axios.get(BASE + "/policies", { headers: authHeader() });
    }

    // Create/refresh all managed policies from the server-side templates (dynamic list + MAINTENANCE).
    seedDefaultPolicies(){
        return axios.post(BASE + "/policies/seed", {}, { headers: authHeader() });
    }

    // Create a new managed policy. kind = MULTI_APP (fler-app POS) | SINGLE_APP (enkelt-app kiosk). Returns allSettings.
    createPolicy(name, kind){
        return axios.post(BASE + "/policySettings/createPolicy", { name, kind }, { headers: authHeader() });
    }

    // Delete a managed policy (structured): removes it from AMAPI + settings. Rejected (400) if a device uses it.
    deleteManagedPolicy(name){
        return axios.post(BASE + "/policySettings/deletePolicy", { name }, { headers: authHeader() });
    }

    // Per-role policy settings → { orientation: { POS, KIOSK, KDS } } (LANDSCAPE|PORTRAIT|AUTO).
    getPolicySettings(){
        return axios.get(BASE + "/policySettings", { headers: authHeader() });
    }

    // Set a role's screen orientation. Re-seed policies afterwards to push it to devices.
    setPolicyOrientation(role, orientation){
        return axios.post(BASE + "/policySettings/orientation", { role, orientation }, { headers: authHeader() });
    }

    // Toggle "keep screen on while charging" (stayOnPluggedModes). Re-seed policies afterwards to apply.
    setStayOnCharging(enabled){
        return axios.post(BASE + "/policySettings/stayOnCharging", { enabled }, { headers: authHeader() });
    }

    // Toggle a role's "hold the screen awake" (managed config keep_screen_on — the app applies it, and only
    // an app new enough to read the key will). Re-seed policies afterwards to push it.
    setKeepScreenOn(role, enabled){
        return axios.post(BASE + "/policySettings/keepScreenOn", { role, enabled }, { headers: authHeader() });
    }

    // Set a role's OS-update mode (WINDOWED | AUTOMATIC). Re-seed policies afterwards to apply.
    setSystemUpdateMode(role, mode){
        return axios.post(BASE + "/policySettings/systemUpdate", { role, mode }, { headers: authHeader() });
    }

    // Set a role's nav bar (NAVIGATION_ENABLED | HOME_BUTTON_ONLY | NAVIGATION_DISABLED). Re-seed afterwards to apply.
    setSystemNavigation(role, mode){
        return axios.post(BASE + "/policySettings/systemNavigation", { role, mode }, { headers: authHeader() });
    }

    // Set a role's Settings-app access (SETTINGS_ACCESS_ALLOWED | SETTINGS_ACCESS_BLOCKED). Re-seed afterwards to apply.
    setDeviceSettings(role, mode){
        return axios.post(BASE + "/policySettings/deviceSettings", { role, mode }, { headers: authHeader() });
    }

    // Set a role's app list ([{packageName, installType}]). Re-seed policies afterwards to apply.
    setApps(role, apps){
        return axios.post(BASE + "/policySettings/apps", { role, apps }, { headers: authHeader() });
    }

    // Set the global OS-update maintenance window (start/end minutes from midnight). Re-seed to apply.
    setUpdateWindow(start, end){
        return axios.post(BASE + "/policySettings/updateWindow", { start, end }, { headers: authHeader() });
    }

    // Set a role's boot auto-launch app + delay (seconds). Re-seed policies afterwards to apply.
    setBootLaunch(role, app, delaySeconds, returnSeconds){
        return axios.post(BASE + "/policySettings/bootLaunch",
            { role, app, delaySeconds, returnSeconds }, { headers: authHeader() });
    }

    // Set a role's local kiosk-exit PIN config (pin blank = keep existing). Re-seed afterwards to apply.
    setLocalExit(role, enabled, pin, relockSeconds){
        return axios.post(BASE + "/policySettings/localExit", { role, enabled, pin, relockSeconds }, { headers: authHeader() });
    }

    // The 06:00 restart of every till, kiosk and KDS -> {enabled, time, policies: [{name, role: "till"|"kiosk"|"kds",
    // startsAfterBoot}], changedAt, changedBy, lastRun}. Its own document, not part of the policy settings.
    getDailyReboot(){
        return axios.get(BASE + "/dailyReboot", { headers: authHeader() });
    }

    // Switch the 06:00 restart on or off. Saved at once and read by the next run; no re-seed. Returns the new state.
    setDailyReboot(enabled){
        return axios.post(BASE + "/dailyReboot", { enabled }, { headers: authHeader() });
    }

    // Link (or clear, target="") the MAINTENANCE-kind policy a policy's devices switch to on "Avslutt kiosk-modus".
    setMaintenancePolicy(name, target){
        return axios.post(BASE + "/policySettings/maintenancePolicy", { role: name, target }, { headers: authHeader() });
    }

    // Delete a policy by id. AMAPI refuses if a device/enrollment-token still references it.
    deletePolicy(policyId){
        return axios.delete(BASE + "/policies/" + encodeURIComponent(policyId), { headers: authHeader() });
    }

    // Returns an EnrollmentToken {value, qrCode, expirationTimestamp, ...}. additionalData is echoed to
    // the device (e.g. the merchant slug to bind at provisioning).
    createEnrollmentToken(policyId, durationSeconds, additionalData){
        return axios.post(BASE + "/enrollmentToken", null, {
            headers: authHeader(),
            params: { policyId, durationSeconds, additionalData: additionalData || undefined }
        });
    }
}

export default new MdmService();
